




































































































import { computed, defineComponent, PropType, ref, toRef, watch } from "@vue/composition-api";
import { format, getMinutes, setMinutes, startOfDay } from "date-fns";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import {
  AppointmentSlot,
  InputAppointmentSlotSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentSlotProvider";
import {
  AppointmentType,
  AppointmentTypeKind,
} from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import useAppointments from "@/hooks/useAppointments";
import { $t, $tc } from "@/lib/i18nHelpers";
import { getFullPriceData } from "@/lib/moneyHelpers";
import { formatDate } from "@/lib/time";
import { ListFields, Tab } from "@/types";
import { PurchaseType } from "@/const";
import { onlineAppointmentKinds } from "@/components/AppointmentTypesSettings/config";
import BaseButton from "@/components/BaseButton";
import BaseInput from "@/components/BaseInput";
import BaseSegmentedButton from "@/components/BaseSegmentedButton";
import BaseSelect from "@/components/BaseSelect";
import BaseSpinner from "@/components/BaseSpinner";
import BaseTabs from "@/components/BaseTabs";
import CustomScroll from "@/components/CustomScroll";
import ModalView from "@/components/ModalView";
import PaymentConfirmation from "@/components/PaymentConfirmation";
import TimePickerButton from "@/components/TimePickerButton";
import UserBadgeWithAvatar from "@/components/UserBadgeWithAvatar";

export default defineComponent({
  name: "UserBookAppointmentModalView",
  components: {
    PaymentConfirmation,
    BaseTabs,
    BaseSelect,
    UserBadgeWithAvatar,
    TimePickerButton,
    BaseSegmentedButton,
    ModalView,
    BaseInput,
    CustomScroll,
    BaseButton,
    BaseSpinner,
  },
  props: {
    loading: { type: Boolean, required: true },
    processing: { type: Boolean, required: true },
    user: { type: Object as PropType<UserPublicData>, required: true },
    isSubscriber: { type: Boolean, required: true },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
    availableAppointmentKinds: { type: Array as () => AppointmentTypeKind[], required: true },
  },
  setup(props, { emit }) {
    const { slotsLoading, typesLoading, appointmentSlots, appointmentTypes, getAppointmentSlots, getAppointmentTypes } =
      useAppointments();

    const selectDate = ref(format(new Date(), "yyyy-MM-dd"));
    const activeSlot = ref<AppointmentSlot>();
    const selectedDuration = ref<number>();
    const selectedAppointmentTypeId = ref("");
    const showConfirm = ref(false);
    const appointmentTypeTabActive = ref("Online");
    const activeTab = ref<AppointmentTypeKind>(AppointmentTypeKind.MESSAGING);

    const getSlotTime = (date: string) => {
      return formatDate(new Date(date), { hour: "numeric", minute: "numeric" });
    };

    const startTime = computed(() => startOfDay(new Date(selectDate.value)).toISOString());
    const endTime = computed(() =>
      startOfDay(new Date(selectDate.value).setDate(new Date(selectDate.value).getDate() + 1)).toISOString()
    );

    const appointmentsFilterByType = computed(() => {
      return appointmentTypes.value?.types.filter(item => item.kind === activeTab.value) ?? [];
    });

    const selectedAppointmentType = computed<AppointmentType | undefined>(() =>
      appointmentsFilterByType.value.find(appointmentType => appointmentType.id === selectedAppointmentTypeId.value)
    );

    const durationOptions = computed<ListFields>(() => {
      const options: ListFields = [];
      new Set(appointmentsFilterByType.value.map(type => type.duration)).forEach(duration => {
        options.push({
          text: duration >= 60 ? $tc($t("hour"), duration / 60) : $t("{0} min", [duration]),
          value: duration,
        });
      });
      return options;
    });

    const priceData = computed(() =>
      getFullPriceData(props.paymentPackages.find(i => i.id === selectedAppointmentType.value?.pricePackageId))
    );

    const bookButtonText = computed(() => {
      if (!selectedAppointmentTypeId.value) {
        return $t("Book");
      }
      return $t("Book for {0}", [priceData.value.creditAmount ? priceData.value.creditPrice : $t("free")]);
    });

    const activeTimeRange = computed(() => {
      const selectedDuration = selectedAppointmentType.value?.duration;
      if (activeSlot.value && selectedDuration) {
        const appointmentEndTime = setMinutes(
          new Date(activeSlot.value.startTime),
          getMinutes(new Date(activeSlot.value.startTime)) + selectedDuration
        );
        return `${getSlotTime(activeSlot.value?.startTime ?? "").toLowerCase()} - ${getSlotTime(
          appointmentEndTime.toISOString()
        ).toLowerCase()}`;
      }
      return undefined;
    });

    const onlineKindsTabs = computed<Tab[]>(() => {
      return [
        { value: AppointmentTypeKind.MESSAGING, text: $t("Chat") },
        { value: AppointmentTypeKind.VIDEO_CALLS, text: $t("Video") },
        { value: AppointmentTypeKind.AUDIO_CALLS, text: $t("Audio") },
      ].filter(item => props.availableAppointmentKinds.includes(item.value));
    });

    const appointmentTypesTabs = computed<Tab[]>(() => {
      const hasOnlineTypes = props.availableAppointmentKinds.some(kind => onlineAppointmentKinds.includes(kind));
      const hasOfflineTypes = props.availableAppointmentKinds.includes(AppointmentTypeKind.TOUR_GUIDE);

      return [
        ...(hasOnlineTypes ? [{ text: $t("Online"), value: "Online" }] : []),
        ...(hasOfflineTypes ? [{ text: $t("Offline"), value: "Offline" }] : []),
      ];
    });

    const selectTime = (slot: AppointmentSlot) => {
      activeSlot.value = slot;
      selectedAppointmentTypeId.value = slot.typeId;
    };

    const onSelectDate = async () => {
      activeSlot.value = undefined;
      selectedDuration.value = undefined;
      selectedAppointmentTypeId.value = "";

      await getAppointmentTypes({
        userIds: [props.user.id],
        startTime: startTime.value,
        endTime: endTime.value,
        forSubscribers: props.isSubscriber,
        checkAdvancedMode: true,
      });

      selectedDuration.value = durationOptions.value[0]?.value;
    };

    const changeActiveTab = (tab: AppointmentTypeKind) => {
      activeTab.value = tab;
      onSelectDate();
    };

    const changeAppointmentTypeTab = (value: string) => {
      appointmentTypeTabActive.value = value;
      if (value === "Offline") {
        changeActiveTab(AppointmentTypeKind.TOUR_GUIDE);
      } else {
        changeActiveTab(onlineKindsTabs.value[0].value);
      }
    };

    const onBookHandler = () => {
      if (!activeSlot.value) return;
      if (priceData.value.creditAmount && !showConfirm.value) {
        showConfirm.value = true;
      } else {
        emit("book", activeSlot.value, () => {
          showConfirm.value = false;
        });
      }
    };

    watch(selectedDuration, async value => {
      activeSlot.value = undefined;
      selectedAppointmentTypeId.value = "";
      if (value) {
        const data: InputAppointmentSlotSearch = {
          userId: props.user.id,
          startTime: startTime.value,
          endTime: endTime.value,
          kind: activeTab.value,
          duration: value,
        };
        await getAppointmentSlots(data);
      }
    });

    const unwatch = watch(toRef(props, "availableAppointmentKinds"), (value, oldValue) => {
      if (value.length && !oldValue.length) {
        changeAppointmentTypeTab(appointmentTypesTabs.value[0].value);
        unwatch();
      }
    });

    return {
      slotsLoading,
      typesLoading,
      onlineKindsTabs,
      appointmentTypesTabs,
      appointmentTypes,
      activeTab,
      selectDate,
      activeSlot,
      showConfirm,
      durationOptions,
      selectedDuration,
      bookButtonText,
      activeTimeRange,
      appointmentSlots,
      selectedAppointmentType,
      appointmentTypeTabActive,
      priceData,
      PurchaseType,
      onSelectDate,
      selectTime,
      getSlotTime,
      changeActiveTab,
      changeAppointmentTypeTab,
      onBookHandler,
    };
  },
});
